import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { isNullOrWhitespace, pathJoin, showErrorSwal } from '@asamco/utils';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { LinkStatusCode } from '../../enums/link-status-code.enum';
import { DashboardItem } from '../../interfaces/dashboard-item.interface';
import { LinkStatus } from '../../interfaces/link-status.interface';
import { Router } from '@angular/router';
import { LinkRunStatus } from '../../enums/link-run-status.enum';
import { LinkStatusSignalrService } from '../../services/signalR/link-status-signalr.service';
import { LinkTabService } from '../../services/link-tab.service';
import { QueueService } from '../../services/queue.service';
import { Queue, QueueTrigger } from '../../interfaces/queue.interface';
import { NgIf, NgStyle, NgClass, AsyncPipe, JsonPipe } from '@angular/common';

@Component({
    selector: 'al-dashboard-tile',
    templateUrl: './dashboard-tile.component.html',
    styleUrls: ['../../styles/alert.scss', '../../styles/tile-style.scss', './dashboard-tile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        NgStyle,
        NgClass,
        AsyncPipe,
		JsonPipe
    ],
})
export class DashboardTileComponent implements OnInit, OnDestroy {
	public imgUrl = '';
	@Input()
	item: DashboardItem;
	@Input()
	linkStatus$: Observable<LinkStatus>;
	linkStatusCode = LinkStatusCode;

	@Input()
	userCanRunLinks: boolean;

	private _azureItem: DashboardItem;

	@Input() set azureItemdata(value: DashboardItem[]) {
		if (value?.length > 0) {
			this._azureItem = value.find(data => data.id == this.item.id);
			if (this._azureItem) {
				this.handleAzureData(this._azureItem);
			}
		}
	}

	public disabledRunLinkButtonSubject = new BehaviorSubject<LinkRunStatus>(LinkRunStatus.undefined);
	public disabledRunLinkButtonObservable: Observable<LinkRunStatus> = this.disabledRunLinkButtonSubject.asObservable();
	public currentStatusSubject: Subject<string> = new BehaviorSubject<string>('There is currently no live status');
	public currentStatusColorSubject: Subject<LinkStatusCode> = new BehaviorSubject<LinkStatusCode>(LinkStatusCode.Undefined);
	public isLinkExpiredSubject = new BehaviorSubject<boolean>(false);
	public isLinkExpiredObservable: Observable<boolean> = this.isLinkExpiredSubject.asObservable();
	public expiryTime: number;

	public isCsp: boolean;
	public menuItems: any[] = [];
	public active: boolean = false;
	public destroy$: Subject<void> = new Subject<void>();

	constructor(
		private cdr: ChangeDetectorRef,
		private router: Router,
		private linkStatusService: LinkStatusSignalrService,
		private linkTabService: LinkTabService,
		private queueService: QueueService
	) {
		this.imgUrl = pathJoin([window['deployUrl'], '/assets/img/']);
	}

	public get linkRunStatus(): typeof LinkRunStatus {
		return LinkRunStatus;
	}

	handleAzureData(item: DashboardItem) {
		if (item.isLinkRunning) {
			this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
		} else {
			this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
		}
	}

	ngOnInit(): void {
		if (this.item.frontendPage) {
			this.isCsp = true;
		}

		this.expiryTime = new Date(this.item.linkExpiryDate).getTime();
		this.currentStatusSubject.next(this.item.status);
		this.currentStatusColorSubject.next(this.item.statusColor);

		this.menuItems = this.linkTabService.getTabs(this.item.id, this.isCsp);

		this.linkStatus$.pipe(filter(linkStatus => linkStatus.linkID === this.item.id)).subscribe(linkStatus => {
			this.item.currentStatus = linkStatus.status;
			this.item.statusColor = linkStatus.statusCode;
			this.cdr.markForCheck();
		});

		this.linkStatusService.onInitialized.pipe(
				switchMap(() => this.linkStatusService.getLinkStatusObservable()),
				takeUntil(this.destroy$),
				filter(linkStatus => linkStatus.linkID === this.item.id)
			)
			.subscribe(linkStatus => {
				if (linkStatus.statusCode != LinkStatusCode.Undefined) {
					//this.currentStatus = linkStatus.status;
					//this.statusCode = linkStatus.statusCode;
					this.currentStatusSubject.next(linkStatus.status);
					this.currentStatusColorSubject.next(linkStatus.statusCode);
					//this.cdr.detectChanges();
				} else {
					//Currently use this to update the linkRunButton
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
				}
			});

		// this.LinkFunctionAppStatusService.isLinkRunning(this.item.id).subscribe(e => {
		// 	//If true: link is running
		// 	if(e)
		// 	{
		// 		this.disabledRunLinkButtonSubject.next(LinkRunStatus.active)
		// 	}
		// 	else
		// 	{
		// 		this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive)
		// 	}}, error =>  { showErrorSwal(error) });

		this.linkStatusService
			.getLinkButtonObservable()
			.pipe(
				takeUntil(this.destroy$),
				filter(linkStatus => linkStatus.linkID == this.item.id)
			)
			.subscribe(linkButtonStatus => {
				if (linkButtonStatus.isLinkRunning) {
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
				} else {
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
				}
			});
	}

	getProperImageRef(item: DashboardItem) {
		if (item.tileHeaderIsFromApp) {
			return '/application-logos/' + item.fromAppHeaderImageRef;
		} else {
			return '/application-logos/' + item.toAppHeaderImageRef;
		}
	}

	getIntegrationTitleName(item: DashboardItem): string {
		if (item.tileHeaderIsFromApp) {
			return item.toDName;
		}
		return item.fromDName;
	}

	navigateToSetup = e => {
		this.router.navigate(['application-link/link/' + this.item.id], { queryParams: { tab: 2 } });
	};

	navigateToInfo = e => {
		this.router.navigate(['application-link/link/' + this.item.id], { queryParams: { tab: 1 } });
	};

	navigateToScheduler = e => {
		this.router.navigate(['application-link/link/' + this.item.id], { queryParams: { tab: 3 } });
	};

	setHeaderColor(item: any) {
		let primaryHeaderItem: string = item.toAppHeaderImageRef;
		if (item.tileHeaderIsFromApp) {
			primaryHeaderItem = item.fromAppHeaderImageRef;
		}

		if (primaryHeaderItem == 'connectwiseheader.png') {
			return 'black';
		} else if (primaryHeaderItem == 'autotaskheader.png') {
			return '#026ccf';
		} else if (primaryHeaderItem == 'adnheader.png') {
			return '#B5B5B3';
		} else if (primaryHeaderItem == 'ingrammicroheader.png') {
			return '#2F75BB';
		} else if (primaryHeaderItem == 'alsoheader.png') {
			return '#6c757d';
		} else if (primaryHeaderItem == 'techdataheader.png') {
			return '#202A45';
		} else if (primaryHeaderItem == 'autotaskheader.png') {
			return '#026ccf';
		} else if (primaryHeaderItem == 'bmsheader.png') {
			return '#193E64';
		} else if (primaryHeaderItem == 'techdataionheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'zohobooksheader.png') {
			return '#4E80B5';
		} else if (primaryHeaderItem == 'infiterraheader.png') {
			return '#100843';
		} else if (primaryHeaderItem == 'xeroheader.png') {
			return '#1aadd2';
		} else if (primaryHeaderItem == 'halopsaheader.png') {
			return '#f9384b';
		} else if (primaryHeaderItem == 'appriverheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'arrowsphereheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'syncroheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'copacoheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'swiss21abaninjaheader.png') {
			return '#FFFFFF';
		}

		return 'black';
	}

	public getUrl(subUrl: string): string {
		if (!isNullOrWhitespace(this.imgUrl) && !isNullOrWhitespace(subUrl)) {
			return pathJoin([this.imgUrl, subUrl]);
		}
		return '';
	}

	pressPlay(): void {
		if (this.item.frontendPage != null && this.item.frontendPage.length > 0) {
			this.toFrontendPage();
		} else {
			this.runLink();
		}
	}

	toFrontendPage(): void {
		this.router.navigate([this.item.frontendPage, this.item.id]);
	}

	runLink(): void {
		this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
		this.currentStatusSubject.next('Link enqueued, awaiting execution');
		this.currentStatusColorSubject.next(LinkStatusCode.Info);

		const queue: Queue = {
			linkId: this.item.id,
			trigger: QueueTrigger.User,
		};

		this.queueService.createQueueEntry(queue).subscribe(
			(result: any) => {
				// Handle result
				//Keep running if everything went ok
			},
			error => {
				showErrorSwal(error);
				console.log(error);
				//TODO: Detailed information?
				this.currentStatusSubject.next('Failed to start link');
				//this.currentStatus = 'Failed to start link';
				//this.statusCode = LinkStatusCode.Danger;
				this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
			}
		)
	}

	public toScheduler() {
		this.router.navigate(['application-link/link/' + this.item.id], { queryParams: { tab: 5 } });
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
